import { ChangeEvent, useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AppXBtn } from '@/UI/AppXBtn/AppXBtn';
import { Input } from '@/UI/Input/Input';
import { FlexCenVer, FlexCol } from '@/UI/styles';
import { TextSmRg } from '@/UI/AppTypography/AppTypography';
import { color } from '@/colors';
import { useIsHovered } from '@/hooks/useIsHovered';
import { I18N_KEYS } from '@/language/keys';
import { getListSelectorIteratorMaxOptions } from '@/utils/listNode';
import { LIST_MAX_CHARS } from '@/components/Nodes/consts/validations.consts';
import { AddItemButton } from '../../Common/AddItemButton/AddItemButton';

interface DesignAppTextIteratorProps {
  id: string;
  value: { options: string[] };
  onChange: (id: string, value: { options: string[] }) => void;
  disabled?: boolean;
  error?: boolean;
  helperText?: string;
}

export default function DesignAppTextIterator({
  id,
  value,
  onChange,
  disabled = false,
  error,
  helperText,
}: DesignAppTextIteratorProps) {
  const { t } = useTranslation();
  const options = useMemo(() => value?.options ?? [''], [value?.options]);
  const inputRefs = useRef<Array<HTMLInputElement | null>>([]);
  const [pendingFocusIndex, setPendingFocusIndex] = useState<number | null>(null);
  const maxOptions = getListSelectorIteratorMaxOptions();

  const emit = useCallback((newOptions: string[]) => onChange(id, { options: newOptions }), [id, onChange]);

  const handleValueChange = useCallback(
    (val: string, index: number) => {
      const next = options.map((o, i) => (i === index ? val : o));
      emit(next);
    },
    [options, emit],
  );

  const handleAddItem = useCallback(() => {
    emit([...options, '']);
    setPendingFocusIndex(options.length);
  }, [options, emit]);

  const handleEnter = useCallback(
    (index: number) => {
      if (options.length >= maxOptions) return;
      const next = [...options.slice(0, index + 1), '', ...options.slice(index + 1)];
      emit(next);
      setPendingFocusIndex(index + 1);
    },
    [options, maxOptions, emit],
  );

  const handleTab = useCallback(
    (index: number, direction: 'next' | 'prev') => {
      if (options.length === 0) return;
      const jump = direction === 'next' ? 1 : -1;
      setPendingFocusIndex((index + jump + options.length) % options.length);
    },
    [options.length],
  );

  const handleDelete = useCallback((index: number) => emit(options.filter((_, i) => i !== index)), [options, emit]);

  const handleDeleteAndFocusPrev = useCallback(
    (index: number) => {
      setPendingFocusIndex(Math.max(0, index - 1));
      handleDelete(index);
    },
    [handleDelete],
  );

  useLayoutEffect(() => {
    if (pendingFocusIndex == null) return;
    const el = inputRefs.current[pendingFocusIndex];
    if (el) {
      el.focus();
      el.select();
      setPendingFocusIndex(null);
    }
  }, [pendingFocusIndex, options]);

  return (
    <FlexCol sx={{ width: '100%', gap: 0.75 }}>
      <FlexCol
        sx={{
          gap: 0.75,
          maxHeight: 240,
          overflow: 'hidden auto',
          py: '2px',
          scrollbarColor: `${color.White16_T} transparent`,
        }}
      >
        {options.map((option, index) => (
          <TextIteratorItem
            key={index}
            item={option}
            index={index}
            onValueChange={handleValueChange}
            onEnter={handleEnter}
            onTab={handleTab}
            inputRef={(el) => {
              inputRefs.current[index] = el;
            }}
            onDelete={handleDelete}
            onDeleteAndFocusPrev={handleDeleteAndFocusPrev}
            editable={!disabled}
            allowDelete={options.length > 1}
            error={error && index === 0}
            helperText={error && index === 0 ? helperText : undefined}
          />
        ))}
      </FlexCol>
      <FlexCenVer sx={{ justifyContent: 'space-between' }}>
        {!disabled && (
          <AddItemButton
            show
            onClick={handleAddItem}
            text={t(I18N_KEYS.RECIPE_MAIN.NODES.LIST_NODE.ITERATOR.ADD_ITEM)}
            disabled={options.length >= maxOptions}
          />
        )}
        <TextSmRg color={color.White40_T}>
          {options.length > maxOptions
            ? t(I18N_KEYS.RECIPE_MAIN.NODES.LIST_NODE.ITERATOR.ITEMS_COUNT, { count: options.length })
            : `${options.length}/${maxOptions}`}
        </TextSmRg>
      </FlexCenVer>
    </FlexCol>
  );
}

function TextIteratorItem({
  editable = false,
  item,
  index,
  onValueChange,
  onEnter,
  onTab,
  inputRef,
  onDelete,
  onDeleteAndFocusPrev,
  allowDelete = true,
  error,
  helperText,
}: {
  editable?: boolean;
  item: string;
  index: number;
  onValueChange: (value: string, index: number) => void;
  onEnter: (index: number) => void;
  onTab: (index: number, direction: 'next' | 'prev') => void;
  inputRef: (el: HTMLInputElement | null) => void;
  onDelete: (index: number) => void;
  onDeleteAndFocusPrev: (index: number) => void;
  allowDelete?: boolean;
  error?: boolean;
  helperText?: string;
}) {
  const { t } = useTranslation();
  const { isHovered, ...hoverProps } = useIsHovered();
  const [isFocused, setIsFocused] = useState(false);

  const handleChange = useCallback(
    (evt: ChangeEvent<HTMLInputElement>) => onValueChange(evt.target.value, index),
    [index, onValueChange],
  );
  const handleDelete = useCallback(() => onDelete(index), [index, onDelete]);

  const handleKeyDown = useCallback(
    (evt: React.KeyboardEvent<HTMLInputElement>) => {
      if (!editable) return;

      if (evt.key === 'Escape') {
        evt.preventDefault();
        evt.stopPropagation();
        (evt.currentTarget as HTMLInputElement).blur();
        return;
      }

      if (evt.key === 'Tab') {
        evt.preventDefault();
        evt.stopPropagation();
        return onTab(index, evt.shiftKey ? 'prev' : 'next');
      }

      if (evt.key === 'Enter') {
        evt.preventDefault();
        evt.stopPropagation();
        if (item.length === 0) return;
        return onEnter(index);
      }

      if (evt.key === 'Backspace' && item.length === 0 && allowDelete) {
        evt.preventDefault();
        evt.stopPropagation();
        onDeleteAndFocusPrev(index);
      }
    },
    [allowDelete, editable, index, item, onDeleteAndFocusPrev, onEnter, onTab],
  );

  return (
    <FlexCenVer sx={{ width: '100%', height: 32 }} {...hoverProps} className={isFocused ? 'nodrag' : ''}>
      <Input
        fullWidth
        disabled={!editable}
        value={item}
        onChange={handleChange}
        inputProps={{ maxLength: LIST_MAX_CHARS }}
        onKeyDown={handleKeyDown}
        noBlurOnEnter
        placeholder={t(I18N_KEYS.RECIPE_MAIN.NODES.LIST_NODE.ITERATOR.PLACEHOLDER, { index: index + 1 })}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        sx={{ pointerEvents: !editable ? 'none' : '' }}
        inputRef={inputRef}
        error={error}
        helperText={helperText}
      />
      {editable && allowDelete && (isHovered || isFocused) ? (
        <AppXBtn onClick={handleDelete} size={20} sx={{ ml: 0.5 }} />
      ) : null}
    </FlexCenVer>
  );
}
